import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import DOMPurify from "dompurify";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const DISMISS_KEY = "top_banner_dismissed";
const SUPPORTED = ["pt", "en", "es", "fr"];

const SANITIZE_OPTS = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "br", "a", "span", "p", "small"],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
};

export function BetaBanner() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const ref = useRef(null);

  const [config, setConfig] = useState(null); // { enabled, audience, allow_dismiss, content }
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );

  useEffect(() => {
    let cancelled = false;
    api.get("/api/settings/top-banner")
      .then((res) => { if (!cancelled) setConfig(res.data || null); })
      .catch(() => { if (!cancelled) setConfig(null); });
    return () => { cancelled = true; };
  }, []);

  const isMaintenance = window.location.pathname === "/manutencao";
  // Public route pages (/rota/{slug}) render the maximized editor full-viewport
  // with no site chrome — the banner must not overlay it.
  const isPublicRoute = window.location.pathname.startsWith("/rota/");

  // Audience gating: anon = only logged-out, auth = only logged-in, all = everyone.
  const audience = config?.audience || "anon";
  const audienceOk =
    audience === "all" ? true : audience === "auth" ? !!user : !user;
  const allowDismiss = config?.allow_dismiss !== false;

  const hidden =
    !config ||
    !config.enabled ||
    !audienceOk ||
    isMaintenance ||
    isPublicRoute ||
    (allowDismiss && dismissed);

  useEffect(() => {
    if (hidden) {
      document.documentElement.style.setProperty("--beta-banner-h", "0px");
      return;
    }
    const update = () => {
      const h = ref.current?.offsetHeight || 0;
      document.documentElement.style.setProperty("--beta-banner-h", `${h}px`);
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      document.documentElement.style.setProperty("--beta-banner-h", "0px");
    };
  }, [hidden, config]);

  if (hidden) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const lang = String(i18n.language || "pt").slice(0, 2).toLowerCase();
  const effLang = SUPPORTED.includes(lang) ? lang : "pt";
  const raw = config.content?.[effLang] || config.content?.pt || "";
  const html = DOMPurify.sanitize(raw, SANITIZE_OPTS);

  return (
    <div
      ref={ref}
      className="fixed left-0 right-0 z-[10000] bg-primary/95 backdrop-blur-sm text-primary-foreground text-xs sm:text-sm"
      style={{ top: "var(--lang-banner-h, 0px)" }}
      data-testid="beta-banner"
    >
      <div className="max-w-7xl mx-auto px-10 sm:px-6 py-2 flex items-center justify-center gap-3 relative">
        <div
          className="text-center leading-relaxed pr-6 top-banner-content [&_a]:underline [&_a]:font-semibold"
          data-testid="top-banner-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {allowDismiss && (
          <button
            onClick={handleDismiss}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/15 hover:bg-white/30 transition-colors"
            aria-label="Fechar"
            data-testid="beta-banner-dismiss-btn"
          >
            <X className="h-4 w-4" strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}
